/**
 * BookingEngineOnboarding.js
 * ----------------------------------------------------------------------------
 * Booking-only onboarding. Runs after BookingEngineSignup (V2 already enabled).
 * Three short steps, each wired to a real V2 endpoint, then it lands the vendor
 * in their booking dashboard.
 *
 *   Step 1  Business      -> PUT  /api/booking-v2/vendor/profile
 *                            { business_sector, default_concurrent_capacity, booking_v2_notification_email }
 *   Step 2  Hours         -> PUT  /api/booking-v2/vendor/business-hours  { businessHours: [...] }
 *                            (prefilled from GET /api/booking-v2/vendor/business-hours — seeded at signup)
 *   Step 3  First service -> POST /api/booking-v2/vendor/services  { serviceName, durationMinutes, ... }
 *                            also GET /api/booking-v2/vendor/config once to ensure a form template exists
 *   Finish                -> GET  /api/booking-v2/vendor/v2-status (progress) + booking link
 *                            "Go to dashboard" -> /vendor/dashboard?tab=bookingV2
 *
 * Add the route in App.js (protected, vendor):
 *   <Route path="/booking-engine/onboarding" element={
 *     <ProtectedRoute allowedRoles={['vendor']}><BookingEngineOnboarding /></ProtectedRoute>
 *   } />
 *
 * IMPORTANT — to actually SEE the Booking V2 tab after landing, relax the gate
 * in VendorDashboard.js getTabs() from:
 *     if (vendor?.business_sector === 'beauty' && vendor?.booking_v2_enabled) { ... }
 * to:
 *     if (vendor?.booking_v2_enabled) { ... }
 * so every booking-engine vendor sees the tab regardless of sector.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../utils/axiosConfig';
import { setAuthToken } from '../utils/auth';

const DAYS = [
  { dow: 1, label: 'Monday' },
  { dow: 2, label: 'Tuesday' },
  { dow: 3, label: 'Wednesday' },
  { dow: 4, label: 'Thursday' },
  { dow: 5, label: 'Friday' },
  { dow: 6, label: 'Saturday' },
  { dow: 0, label: 'Sunday' },
];

const SECTORS = [
  { value: 'beauty', label: 'Beauty & spa' },
  { value: 'health', label: 'Health & wellness' },
  { value: 'general', label: 'Something else' },
];

function BookingEngineOnboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [profile, setProfile] = useState({ subUrl: '', email: '' });
  const [biz, setBiz] = useState({ sector: 'beauty', capacity: 3, notifyEmail: '' });
  const [hours, setHours] = useState(
    DAYS.map((d) => ({
      day_of_week: d.dow,
      is_open: d.dow >= 1 && d.dow <= 5,
      open_time: '09:00',
      close_time: '17:00',
    }))
  );
  const [service, setService] = useState({ name: '', duration: 60, price: '', category: 'General' });
  const [status, setStatus] = useState(null);

  // Prefill from the vendor profile + seeded hours.
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) setAuthToken(token);

    (async () => {
      try {
        const [p, h] = await Promise.all([
          axios.get('/api/booking-v2/vendor/profile'),
          axios.get('/api/booking-v2/vendor/business-hours'),
        ]);
        setProfile({ subUrl: p.data.sub_url || '', email: p.data.email || '' });
        setBiz((b) => ({
          ...b,
          sector: p.data.business_sector || 'beauty',
          capacity: p.data.default_concurrent_capacity || 3,
          notifyEmail: p.data.booking_v2_notification_email || p.data.email || '',
        }));
        if (Array.isArray(h.data.businessHours) && h.data.businessHours.length) {
          setHours(
            DAYS.map((d) => {
              const row = h.data.businessHours.find((r) => r.day_of_week === d.dow);
              return {
                day_of_week: d.dow,
                is_open: row ? row.is_open : d.dow >= 1 && d.dow <= 5,
                open_time: (row?.open_time || '09:00').slice(0, 5),
                close_time: (row?.close_time || '17:00').slice(0, 5),
              };
            })
          );
        }
      } catch (err) {
        console.error('Prefill error:', err);
      }
    })();
  }, []);

  const saveBusiness = async () => {
    setSaving(true);
    setError('');
    try {
      await axios.put('/api/booking-v2/vendor/profile', {
        business_sector: biz.sector,
        default_concurrent_capacity: Number(biz.capacity) || 1,
        booking_v2_notification_email: biz.notifyEmail.trim(),
      });
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save your business details.');
    } finally {
      setSaving(false);
    }
  };

  const saveHours = async () => {
    setSaving(true);
    setError('');
    try {
      // open_time < close_time is enforced even on closed days, so keep valid times throughout.
      await axios.put('/api/booking-v2/vendor/business-hours', { businessHours: hours });
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save your hours.');
    } finally {
      setSaving(false);
    }
  };

  const finish = async () => {
    setSaving(true);
    setError('');
    try {
      if (service.name.trim()) {
        await axios.post('/api/booking-v2/vendor/services', {
          serviceName: service.name.trim(),
          durationMinutes: Number(service.duration) || 60,
          price: service.price === '' ? null : Number(service.price),
          category: service.category || 'General',
        });
      }
      // Ensure a form template exists (this GET creates a default one if missing).
      await axios.get('/api/booking-v2/vendor/config').catch(() => {});
      const s = await axios.get('/api/booking-v2/vendor/v2-status');
      setStatus(s.data);
      setStep(4);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not add your service.');
    } finally {
      setSaving(false);
    }
  };

  const setHour = (dow, key, val) =>
    setHours((prev) => prev.map((h) => (h.day_of_week === dow ? { ...h, [key]: val } : h)));

  // Public, customer-facing vendor page (includes the booking section).
  // NOTE: /widget-test-v2/:subUrl is only a dev test harness — don't share that one.
  const bookingLink = profile.subUrl
    ? `${window.location.origin}/vendor/${profile.subUrl}`
    : '';

  const goToDashboard = useCallback(
    () => navigate('/vendor/dashboard?tab=bookingV2'),
    [navigate]
  );

  return (
    <div className="beo">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;700&family=Inter:wght@400;500;600&display=swap');
        .beo { --ink:#11202e; --mint:#2fb89a; --mint-deep:#1b6f57; --amber:#ffb454;
          --slate:#5c6b73; --line:#e3ebe9; --surface:#f5f8f7;
          min-height:100vh; background:var(--surface); color:var(--ink);
          font-family:'Inter',system-ui,sans-serif; padding:36px 20px; }
        .beo * { box-sizing:border-box; }
        .beo-wrap { max-width:560px; margin:0 auto; }
        .beo-steps { display:flex; gap:8px; margin-bottom:24px; }
        .beo-steps span { flex:1; height:5px; border-radius:999px; background:var(--line); }
        .beo-steps span.on { background:var(--mint); }
        .beo-card { background:#fff; border:1px solid var(--line); border-radius:18px;
          padding:30px 28px; box-shadow:0 24px 50px -32px rgba(17,32,46,.35); }
        .beo-eyebrow { font-family:'Space Grotesk',sans-serif; font-size:12px; font-weight:500;
          letter-spacing:.14em; text-transform:uppercase; color:var(--mint-deep); }
        .beo h1 { font-family:'Space Grotesk',sans-serif; font-weight:700; letter-spacing:-.02em;
          font-size:24px; margin:8px 0 6px; }
        .beo .sub { color:var(--slate); font-size:14px; margin:0 0 22px; }
        .beo label { display:block; font-size:13px; font-weight:600; margin:14px 0 6px; }
        .beo input, .beo select { width:100%; padding:11px 12px; border:1px solid var(--line);
          border-radius:10px; font-size:15px; font-family:inherit; background:#fff; }
        .beo input:focus, .beo select:focus { outline:3px solid rgba(47,184,154,.35); outline-offset:1px; border-color:var(--mint); }
        .beo .row2 { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
        .beo .err { background:#fdecec; color:#b23b3b; padding:10px 12px; border-radius:9px; font-size:13px; margin-top:16px; }
        .beo-actions { display:flex; justify-content:space-between; align-items:center; margin-top:24px; }
        .beo-btn { padding:12px 22px; border:none; border-radius:11px; font-weight:600; font-size:15px; cursor:pointer; }
        .beo-btn.primary { background:var(--ink); color:#fff; }
        .beo-btn.primary:disabled { opacity:.6; cursor:default; }
        .beo-btn.ghost { background:none; color:var(--slate); padding:12px 4px; }
        .beo-skip { background:none; border:none; color:var(--slate); font-size:13px; cursor:pointer; text-decoration:underline; }
        .beo-hours { border:1px solid var(--line); border-radius:12px; overflow:hidden; }
        .beo-day { display:grid; grid-template-columns:1.1fr auto auto; align-items:center; gap:10px;
          padding:10px 12px; border-bottom:1px solid var(--line); font-size:14px; }
        .beo-day:last-child { border-bottom:none; }
        .beo-day.closed { opacity:.5; }
        .beo-day .name { display:flex; align-items:center; gap:8px; font-weight:500; }
        .beo-day input[type=time] { width:108px; padding:7px 8px; }
        .beo-done-stat { display:flex; align-items:baseline; gap:10px; margin:6px 0 18px; }
        .beo-done-stat b { font-family:'Space Grotesk',sans-serif; font-size:34px; color:var(--mint-deep); }
        .beo-link { display:flex; gap:8px; margin:14px 0 4px; }
        .beo-link input { font-size:13px; color:var(--slate); }
        .beo-link button { white-space:nowrap; padding:0 14px; border-radius:10px; border:1px solid var(--line); background:#fff; cursor:pointer; font-weight:600; }
        @media (max-width:520px){ .beo .row2{ grid-template-columns:1fr; } }
      `}</style>

      <div className="beo-wrap">
        <div className="beo-steps">
          {[1, 2, 3].map((n) => (
            <span key={n} className={step >= n ? 'on' : ''} />
          ))}
        </div>

        <div className="beo-card">
          {step === 1 && (
            <>
              <div className="beo-eyebrow">Step 1 of 3 · Your business</div>
              <h1>How do you take bookings?</h1>
              <p className="sub">This shapes your calendar and where booking alerts land.</p>

              <label htmlFor="beo-sector">Business type</label>
              <select id="beo-sector" value={biz.sector}
                onChange={(e) => setBiz({ ...biz, sector: e.target.value })}>
                {SECTORS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>

              <label htmlFor="beo-cap">How many bookings can you run at once?</label>
              <input id="beo-cap" type="number" min="1" value={biz.capacity}
                onChange={(e) => setBiz({ ...biz, capacity: e.target.value })} />

              <label htmlFor="beo-notify">Where should new booking alerts go?</label>
              <input id="beo-notify" type="email" value={biz.notifyEmail}
                onChange={(e) => setBiz({ ...biz, notifyEmail: e.target.value })}
                placeholder="you@business.com" />

              {error && <div className="err">{error}</div>}
              <div className="beo-actions">
                <span />
                <button className="beo-btn primary" disabled={saving} onClick={saveBusiness}>
                  {saving ? 'Saving…' : 'Continue'}
                </button>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div className="beo-eyebrow">Step 2 of 3 · Hours</div>
              <h1>When are you open?</h1>
              <p className="sub">Clients only ever see slots inside these hours.</p>

              <div className="beo-hours">
                {hours.map((h) => {
                  const label = DAYS.find((d) => d.dow === h.day_of_week)?.label;
                  return (
                    <div key={h.day_of_week} className={`beo-day ${h.is_open ? '' : 'closed'}`}>
                      <span className="name">
                        <input type="checkbox" checked={h.is_open}
                          onChange={(e) => setHour(h.day_of_week, 'is_open', e.target.checked)} />
                        {label}
                      </span>
                      <input type="time" value={h.open_time} disabled={!h.is_open}
                        onChange={(e) => setHour(h.day_of_week, 'open_time', e.target.value)} />
                      <input type="time" value={h.close_time} disabled={!h.is_open}
                        onChange={(e) => setHour(h.day_of_week, 'close_time', e.target.value)} />
                    </div>
                  );
                })}
              </div>

              {error && <div className="err">{error}</div>}
              <div className="beo-actions">
                <button className="beo-btn ghost" onClick={() => setStep(1)}>Back</button>
                <button className="beo-btn primary" disabled={saving} onClick={saveHours}>
                  {saving ? 'Saving…' : 'Continue'}
                </button>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <div className="beo-eyebrow">Step 3 of 3 · First service</div>
              <h1>Add a service clients can book</h1>
              <p className="sub">You can add more and fine-tune everything later.</p>

              <label htmlFor="beo-svc">Service name</label>
              <input id="beo-svc" type="text" value={service.name}
                onChange={(e) => setService({ ...service, name: e.target.value })}
                placeholder="e.g. Cut & blow-dry" />

              <div className="row2">
                <div>
                  <label htmlFor="beo-dur">Length (minutes)</label>
                  <input id="beo-dur" type="number" min="5" step="5" value={service.duration}
                    onChange={(e) => setService({ ...service, duration: e.target.value })} />
                </div>
                <div>
                  <label htmlFor="beo-price">Price (optional)</label>
                  <input id="beo-price" type="number" min="0" value={service.price}
                    onChange={(e) => setService({ ...service, price: e.target.value })}
                    placeholder="R" />
                </div>
              </div>

              {error && <div className="err">{error}</div>}
              <div className="beo-actions">
                <button className="beo-btn ghost" onClick={() => setStep(2)}>Back</button>
                <button className="beo-btn primary" disabled={saving} onClick={finish}>
                  {saving ? 'Finishing…' : 'Finish setup'}
                </button>
              </div>
              <div style={{ textAlign: 'right', marginTop: 10 }}>
                <button className="beo-skip" onClick={finish} disabled={saving}>Skip for now</button>
              </div>
            </>
          )}

          {step === 4 && (
            <>
              <div className="beo-eyebrow">You're live</div>
              <h1>Your booking page is ready</h1>
              {status && (
                <div className="beo-done-stat">
                  <b>{status.setupPercentage}%</b>
                  <span className="sub" style={{ margin: 0 }}>
                    setup complete — finish the rest anytime in your dashboard.
                  </span>
                </div>
              )}

              {bookingLink && (
                <>
                  <label>Share this link with clients</label>
                  <div className="beo-link">
                    <input type="text" readOnly value={bookingLink} onFocus={(e) => e.target.select()} />
                    <button onClick={() => navigator.clipboard?.writeText(bookingLink)}>Copy</button>
                  </div>
                </>
              )}

              <div className="beo-actions">
                <span />
                <button className="beo-btn primary" onClick={goToDashboard}>
                  Go to your booking dashboard
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default BookingEngineOnboarding;